import { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Cell, LabelList,
} from 'recharts';
import { fmtMoney } from './format';
import { c, t, sp, r, chart } from '../../theme';

// Parts value by category, ranked, each bar split into what has moved in the
// last twelve months and what has not.
//
// ── Why horizontal ────────────────────────────────────────────────────────────
// Category names are long — "Belt Conveyor System", "Chain Conveyor System" —
// and thirteen of them on a vertical axis either overlap or get angled to the
// point of being unreadable. Horizontal also matches how the chart is read:
// down a ranked list, same direction as the table beneath it.
//
// ── Why not a pie ─────────────────────────────────────────────────────────────
// There are ~90 categories. Even folded to a handful of slices, a pie needs the
// all-pairs-validated `chart.coVisible` set, which has four slots — fewer than
// the categories worth showing. Using the seven-slot categorical set in a pie
// is the exact bug the theme file warns about: two of its hues sit 1.3 ΔE apart
// under deuteranopia. A ranked bar needs no such palette at all, because length
// carries the comparison and color carries only the idle split.
//
// ── Why the idle split ────────────────────────────────────────────────────────
// Value alone ranks Brake System ($147k) and Idle Roller ($138k) as near
// equals. They are not: Brake System is 18% idle, Idle Roller is 100% idle.
// One is stock turning over, the other is capital sitting still. A single-color
// bar hides the only part of this that changes a decision.
//
// ── Color ─────────────────────────────────────────────────────────────────────
// Two segments, stacked, so adjacent-pair validation applies and
// `chart.categorical` is the correct set to draw from. Idle takes the same
// orange that AgeBandByCostCenter gives "Aged +12 mo", so the color means the
// same thing on both charts. The active remainder takes the recessive navy
// rather than a second hue — it is a remainder, not a competing identity.
// The pair was run through the palette validator: adjacent CVD ΔE 18.0
// (protan), normal-vision ΔE 29.2, both marks above 3:1 against the surface.
const ACTIVE_FILL = chart.recessive;
const IDLE_FILL   = chart.categorical[1];

// Past a dozen the labels crowd and the ranking stops being legible. The tail
// is summed into one bar rather than dropped, so the chart's total still
// reconciles with the KPI tile — the same rule AgeBandByCostCenter follows.
const MAX_BARS = 12;

export default function PartCategoryChart({ rows, onPick, selected }) {
  const [expanded, setExpanded] = useState(false);
  if (!rows?.length) return null;

  const ranked = [...rows]
    .map((row) => {
      const value = Number(row.value || 0);
      const idle  = Number(row.aged_value || 0);
      return {
        label:  row.part_category,
        value,
        idle,
        // Clamped at zero: aged_value is summed independently, and a rounding
        // disagreement must never render as a negative segment pointing the
        // wrong way down the axis.
        active: Math.max(0, value - idle),
        items:  Number(row.item_count || 0),
      };
    })
    .sort((a, b) => b.value - a.value);

  // Expanded shows every category as its own bar. The fold is a default, not a
  // ceiling — the summed bar answers "how much is down there", and clicking it
  // is the natural way to ask "which ones", so that is what it does.
  const head = expanded ? ranked : ranked.slice(0, MAX_BARS);
  const tail = expanded ? [] : ranked.slice(MAX_BARS);

  if (tail.length) {
    head.push(tail.reduce((acc, row) => ({
      label:  `+${tail.length} smaller — click to expand`,
      value:  acc.value + row.value,
      idle:   acc.idle + row.idle,
      active: acc.active + row.active,
      items:  acc.items + row.items,
      isTail: true,
    }), { value: 0, idle: 0, active: 0, items: 0 }));
  }

  const total    = ranked.reduce((sum, row) => sum + row.value, 0);
  const idleTotal = ranked.reduce((sum, row) => sum + row.idle, 0);

  return (
    <div>
      <div style={s.head}>
        <div style={s.title}>Value by Part Category</div>
        <div style={s.sub}>
          {fmtMoney(total)} across {ranked.length} categories ·{' '}
          {fmtMoney(idleTotal)} idle 12+ months
          {tail.length ? ` · smallest ${tail.length} combined` : ''}
        </div>
      </div>

      <div style={s.chartWrap}>
        <ResponsiveContainer width="100%" height={Math.max(260, head.length * (expanded ? 21 : 30) + 70)}>
          <BarChart
            data={head}
            layout="vertical"
            margin={{ top: 4, right: 78, bottom: 4, left: 8 }}
            barCategoryGap={6}
          >
            <CartesianGrid horizontal={false} stroke={chart.grid} />
            <XAxis
              type="number"
              tickFormatter={fmtMoney}
              tick={{ fontSize: 10, fill: chart.axisText }}
              axisLine={false} tickLine={false}
            />
            {/* interval={0} forces every category to render — Recharts drops
                alternate labels to make room otherwise, which silently hides
                rows from the reader without any sign that it happened. */}
            <YAxis
              type="category"
              dataKey="label"
              tick={{ fontSize: 11, fill: chart.axisTextStrong }}
              axisLine={false} tickLine={false} interval={0} width={148}
            />
            <Tooltip
              cursor={{ fill: 'rgba(27,42,74,0.05)' }}
              content={<CategoryTooltip />}
            />
            <Legend
              wrapperStyle={{ fontSize: '11px', paddingTop: '4px' }}
              iconType="square" iconSize={9}
            />
            {/* A 2px surface stroke is what puts a gap between the two fills;
                without it the segments abut and the boundary reads as a shade
                change rather than a cost center. */}
            <Bar
              dataKey="active" name="Used in last 12 months" stackId="v"
              fill={ACTIVE_FILL} stroke={chart.surface} strokeWidth={2}
              isAnimationActive={false}
              onClick={(d) => (d.isTail ? setExpanded(true) : onPick && onPick(d.label))}
              style={{ cursor: 'pointer' }}
            >
              {head.map((row) => (
                <Cell
                  key={row.label}
                  fillOpacity={selected && row.label !== selected ? 0.35 : 1}
                />
              ))}
            </Bar>
            <Bar
              dataKey="idle" name="Idle 12+ months" stackId="v"
              fill={IDLE_FILL} stroke={chart.surface} strokeWidth={2}
              radius={[0, 4, 4, 0]}
              isAnimationActive={false}
              onClick={(d) => (d.isTail ? setExpanded(true) : onPick && onPick(d.label))}
              style={{ cursor: 'pointer' }}
            >
              {head.map((row) => (
                <Cell
                  key={row.label}
                  fillOpacity={selected && row.label !== selected ? 0.35 : 1}
                />
              ))}
              {/* Direct value labels rather than a number on every segment:
                  the total is the figure people read off a ranked bar, and the
                  split is what the color already shows. They also supply the
                  relief the recessive fill's sub-3:1 contrast requires. */}
              <LabelList
                dataKey="value" position="right"
                formatter={fmtMoney}
                style={{ fontSize: 10, fill: c.textMuted }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {ranked.length > MAX_BARS && (
        <div style={s.foot}>
          <span>
            {expanded
              ? `All ${ranked.length} categories`
              : `Top ${MAX_BARS} of ${ranked.length} — the rest are summed into the last bar`}
          </span>
          <button style={s.footBtn} onClick={() => setExpanded((v) => !v)}>
            {expanded ? `Show top ${MAX_BARS}` : `Expand all ${ranked.length}`}
          </button>
        </div>
      )}
    </div>
  );
}

// Own tooltip rather than the default: the useful figure here is the idle
// PERCENTAGE, which is nowhere in the data and is the whole reason the bars are
// split. Recharts' default would list two numbers and leave the reader to
// divide them.
function CategoryTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const pct = row.value ? Math.round((row.idle / row.value) * 100) : 0;
  return (
    <div style={s.tip}>
      <div style={s.tipTitle}>{row.label}</div>
      <div style={s.tipRow}>
        <span style={{ ...s.swatch, background: IDLE_FILL }} />
        Idle 12+: {fmtMoney(row.idle)} ({pct}%)
      </div>
      <div style={s.tipRow}>
        <span style={{ ...s.swatch, background: ACTIVE_FILL }} />
        Used in last 12 months: {fmtMoney(row.active)}
      </div>
      <div style={s.tipTotal}>{fmtMoney(row.value)} · {row.items.toLocaleString('en-US')} lines</div>
    </div>
  );
}

const s = {
  head:      { padding: `${sp[4]} ${sp[5]} 0`, display: 'flex', alignItems: 'baseline', gap: sp[3], flexWrap: 'wrap' },
  title:     { ...t.h3, color: c.navy },
  sub:       { ...t.micro, color: c.textFaint },
  chartWrap: { padding: `${sp[2]} ${sp[5]} ${sp[3]}` },
  foot: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: sp[3], padding: `${sp[2]} ${sp[5]}`, ...t.micro, color: c.textFaint,
  },
  footBtn: {
    background: 'none', border: 'none', padding: 0, font: 'inherit',
    color: c.navy, cursor: 'pointer', fontWeight: 600,
  },

  tip: {
    background: chart.surface, border: `1px solid ${chart.tooltipBorder}`,
    borderRadius: r.lg, padding: `${sp[2]} ${sp[3]}`, ...t.micro, color: c.textStrong,
    boxShadow: '0 4px 14px rgba(16,24,40,0.10)',
  },
  tipTitle: { fontWeight: 600, marginBottom: '4px', color: c.navy },
  tipRow:   { display: 'flex', alignItems: 'center', gap: sp[2], lineHeight: 1.7 },
  tipTotal: { marginTop: '4px', paddingTop: '4px', borderTop: `1px solid ${chart.tooltipBorder}`, color: c.textMuted },
  swatch:   { width: '9px', height: '9px', borderRadius: '2px', display: 'inline-block' },
};
